import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { chatApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  MessageSquare,
  ArrowLeft,
  Send,
  Heart,
  MessageCircle,
  Search,
  MapPin,
  HelpCircle,
  ThumbsUp,
  Loader2,
} from 'lucide-react';

const categories = [
  { id: 'all', label: 'All', icon: MessageSquare },
  { id: 'general', label: 'General', icon: MessageSquare },
  { id: 'question', label: 'Questions', icon: HelpCircle },
  { id: 'recommendation', label: 'Tips', icon: ThumbsUp },
  { id: 'lost_found', label: 'Lost & Found', icon: Search },
];

export default function CommunityChat() {
  const { user, activeBuilding } = useAuth();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const messagesEndRef = useRef(null);

  const hoaId = user?.hoa_id || activeBuilding?.hoa_id || null;
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (hoaId) {
      fetchMessages();
    } else {
      setLoading(false);
    }
  }, [activeCategory, hoaId]);

  const fetchMessages = async () => {
    if (!hoaId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setErrorMessage(null);
      const category = activeCategory === 'all' ? null : activeCategory;
      const data = await chatApi.list(hoaId, category);
      setMessages(Array.isArray(data) ? data : []);
      // Mark this building's chat as read (fire-and-forget; ignore errors)
      chatApi.markRead(hoaId).catch(() => {});
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
      const msg = error?.message || 'Could not load messages — please refresh';
      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Removed demo-data fallback. Messaging now requires a real building.

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    if (!hoaId) {
      toast.error('Join a building to post messages');
      return;
    }

    setSending(true);
    try {
      await chatApi.create({
        hoa_id: hoaId,
        content: newMessage,
        category: activeCategory === 'all' ? 'general' : activeCategory,
      });
      setNewMessage('');
      fetchMessages();
      toast.success('Message posted!');
    } catch (error) {
      console.error('Error posting message:', error);
      toast.error(error?.message || 'Could not post message — please try again');
    } finally {
      setSending(false);
    }
  };

  const handleLike = async (messageId) => {
    try {
      await chatApi.like(messageId);
      setMessages(messages.map(m =>
        m.message_id === messageId ? { ...m, likes: (m.likes || 0) + 1 } : m
      ));
    } catch (error) {
      console.error('Error liking message:', error);
      toast.error(error?.message || 'Could not like message');
    }
  };

  const handleReply = async (messageId) => {
    if (!replyText.trim()) return;

    try {
      await chatApi.reply(messageId, replyText);
      // Reload to get authoritative reply data (server-assigned ids/timestamps)
      fetchMessages();
      setReplyText('');
      setReplyingTo(null);
      toast.success('Reply posted!');
    } catch (error) {
      console.error('Error posting reply:', error);
      toast.error(error?.message || 'Could not post reply — please try again');
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;

    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getCategoryIcon = (category) => {
    const cat = categories.find(c => c.id === category);
    return cat?.icon || MessageSquare;
  };

  const getCategoryColor = (category) => {
    const colors = {
      general: 'bg-slate-100 text-slate-600',
      question: 'bg-purple-100 text-purple-700',
      recommendation: 'bg-emerald-100 text-emerald-700',
      lost_found: 'bg-amber-100 text-amber-700',
    };
    return colors[category] || colors.general;
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center gap-3">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <h1 className="text-lg font-semibold font-manrope text-slate-900">Community Chat</h1>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6 space-y-4">
        {!hoaId ? (
          <Card data-testid="no-building-card">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">No building linked</h3>
              <p className="text-slate-500 text-sm mb-4">
                Your account isn't connected to a building yet. Join a building to chat with your community.
              </p>
              <Link to="/find-building">
                <Button className="bg-sky-500 hover:bg-sky-600" data-testid="join-building-btn">Find a building</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
        <>
        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
          {categories.map((cat) => (
            <Button
              key={cat.id}
              variant={activeCategory === cat.id ? 'default' : 'outline'}
              size="sm"
              className={`rounded-full whitespace-nowrap ${activeCategory === cat.id ? 'bg-slate-900' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
              data-testid={`category-${cat.id}`}
            >
              <cat.icon className="w-4 h-4 mr-1" />
              {cat.label}
            </Button>
          ))}
        </div>

        {/* New Message */}
        <Card data-testid="new-message-card">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <Avatar className="w-10 h-10">
                <AvatarFallback className="bg-sky-100 text-sky-700">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 space-y-2">
                <Input
                  placeholder="Share something with your community..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                  className="border-0 bg-slate-100 focus-visible:ring-0"
                  data-testid="message-input"
                />
                <div className="flex justify-end">
                  <Button 
                    size="sm"
                    className="rounded-full bg-sky-500 hover:bg-sky-600"
                    onClick={handleSend}
                    disabled={sending || !newMessage.trim()}
                    data-testid="send-btn"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Messages */}
        <div className="space-y-3">
          {loading ? (
            <>
              <Skeleton className="h-32 rounded-xl" />
              <Skeleton className="h-32 rounded-xl" />
              <Skeleton className="h-32 rounded-xl" />
            </>
          ) : messages.length > 0 ? (
            messages.map((message, index) => {
              const CatIcon = getCategoryIcon(message.category);
              return (
                <Card 
                  key={message.message_id}
                  className="overflow-hidden animate-slideUp"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`message-${message.message_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex gap-3">
                      <Avatar className="w-10 h-10">
                        <AvatarFallback className="bg-slate-200 text-slate-600">
                          {message.user_name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-slate-900">{message.user_name}</span>
                          <span className="text-xs text-slate-400">{formatTime(message.created_at)}</span>
                        </div>
                        <Badge className={`text-xs mb-2 ${getCategoryColor(message.category)}`}>
                          <CatIcon className="w-3 h-3 mr-1" />
                          {message.category.replace('_', ' ')}
                        </Badge>
                        <p className="text-slate-700">{message.content}</p>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-4 mt-3">
                          <button 
                            className="flex items-center gap-1 text-sm text-slate-500 hover:text-red-500 transition-colors"
                            onClick={() => handleLike(message.message_id)}
                          >
                            <Heart className="w-4 h-4" />
                            <span>{message.likes}</span>
                          </button>
                          <button 
                            className="flex items-center gap-1 text-sm text-slate-500 hover:text-sky-500 transition-colors"
                            onClick={() => setReplyingTo(message.message_id)}
                          >
                            <MessageCircle className="w-4 h-4" />
                            <span>{message.replies?.length || 0}</span>
                          </button>
                        </div>

                        {/* Replies */}
                        {message.replies?.length > 0 && (
                          <div className="mt-4 space-y-3 pl-4 border-l-2 border-slate-100">
                            {message.replies.map((reply, idx) => (
                              <div key={idx} className="flex gap-2">
                                <Avatar className="w-6 h-6">
                                  <AvatarFallback className="text-xs bg-slate-100">
                                    {reply.user_name.charAt(0)}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-slate-900">{reply.user_name}</span>
                                    <span className="text-xs text-slate-400">{formatTime(reply.created_at)}</span>
                                  </div>
                                  <p className="text-sm text-slate-600">{reply.content}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Reply Input */}
                        {replyingTo === message.message_id && (
                          <div className="mt-3 flex gap-2">
                            <Input
                              placeholder="Write a reply..."
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleReply(message.message_id)}
                              className="flex-1 h-9 text-sm"
                              autoFocus
                              data-testid="reply-input"
                            />
                            <Button 
                              size="sm"
                              className="bg-sky-500 hover:bg-sky-600"
                              onClick={() => handleReply(message.message_id)}
                              data-testid="reply-btn"
                            >
                              Reply
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Card data-testid="messages-empty-card">
              <CardContent className="p-8 text-center">
                <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="font-semibold text-slate-900 mb-1">
                  {errorMessage ? 'Couldn\'t load messages' : 'No messages yet'}
                </h3>
                <p className="text-slate-500 text-sm">
                  {errorMessage || 'Start the conversation!'}
                </p>
                {errorMessage && (
                  <Button size="sm" variant="outline" className="mt-4" onClick={fetchMessages} data-testid="retry-messages-btn">
                    Try again
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
        </>
        )}
      </main>
    </div>
  );
}
